import React from 'react';
import type { StaticContext } from '@react-router';
import { NoSsr } from '@shared/components/NoSsr';
import { useIsGuestSiteSsrEnabled } from '@apps/guest/shared/hooks/useIsGuestSiteSsrEnabled';
import { GuestSiteHeadCsr } from './GuestSiteHeadCsr';
import { LazyGuestSiteBody } from './GuestSiteBody.lazy';
import { useNRGuestAttributes } from './hooks/useNRGuestAttributes';
import { BundledApplicatorProvider } from '@shared/utils/styleApplicator/ThemeStylesProvider';

export interface GuestSiteProps
  extends Readonly<{
    eventHandle: string;
    staticContext: StaticContext | undefined;
  }> {}

/**
 * Main GuestSite component.
 *
 * When websiteFullySsrGuestSite is enabled:
 * - GuestSiteBody renders during SSR — auth resolves eagerly, above-the-fold
 *   content (header, welcome card) is included in the initial HTML.
 * - GuestSiteHead is rendered inside GuestSiteBody with event data from the
 *   consolidated EventWebsiteGuest query (no separate head query).
 * - Below-the-fold content is deferred via NoSsr in the layout components.
 *
 * When disabled (default / fallback):
 * - GuestSiteHeadCsr renders during SSR for SEO meta tags (lightweight query).
 * - GuestSiteBody is client-only (wrapped in NoSsr).
 */
const GuestSite: React.FC<GuestSiteProps> = ({ eventHandle, staticContext }) => {
  useNRGuestAttributes(eventHandle);

  const isFullySsrEnabled = useIsGuestSiteSsrEnabled();

  // PRNT-4898: the page-wide applicator latch. Above the body (layout writers) AND the head
  // (StyleApplicatorScript, rendered inside GuestSiteBody) so both read one decision.
  if (isFullySsrEnabled) {
    return (
      <BundledApplicatorProvider>
        <LazyGuestSiteBody
          eventHandle={eventHandle}
          staticContext={staticContext}
        />
      </BundledApplicatorProvider>
    );
  }

  // Fallback: SSR only the head for SEO, body is client-only
  return (
    <BundledApplicatorProvider>
      <GuestSiteHeadCsr eventHandle={eventHandle} />
      <NoSsr>
        <LazyGuestSiteBody
          eventHandle={eventHandle}
          staticContext={staticContext}
        />
      </NoSsr>
    </BundledApplicatorProvider>
  );
};

GuestSite.displayName = 'GuestSite';

export { GuestSite };
